import { Component, OnInit, ChangeDetectorRef, AfterContentChecked, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AustralianState } from 'src/app/shared/models/australian-states.model';
import { ApplicationService } from 'src/app/shared/services/application.service';
import { StudentDetails } from '../models/student-details';
import { SingleResultApiResponse } from 'src/app/shared/models/api-response.model';
import { Store } from '@ngrx/store';
import { Action_ApplicationSectionSaved } from '../state/actions';
import { ErrorResponse } from 'src/app/shared/models/error.model';
import { Observable, of, Subscription } from 'rxjs';
import { ModalService } from 'src/app/shared/services/modal.service';
import { Country, Tab_Title } from 'src/app/shared/models/lookup-data.model';
import { PersonDetails, PersonDetailsConfig, PersonDetailsErrors } from '../shared/person-details/person-details.model';
import { PhoneNumberDetails, PhoneNumberConfig, PhoneNumberErrors } from '../shared/phone-number/phone-number.model';
import { AddressErrors, Address, AddressConfig } from '../shared/address/address.model';
import handleGenericHttpError from '../shared/utils/HandleGenericHttpError';
import { compareStudentDetails } from '../shared/utils/ModelUtils';
import { slideInOut } from '../animations/slideInOut';
import { FormService } from 'src/app/shared/services/form.service';
import { Title } from '@angular/platform-browser';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
  selector: 'online-app-student-details',
  templateUrl: './student-details.component.html',
  styleUrls: ['./student-details.component.scss'],
  host: {
    class: 'application-section-component'
  },
  animations: [slideInOut]
})
export class StudentDetailsComponent implements OnInit, AfterContentChecked, AfterViewInit, OnDestroy {
  studentDetailsFormGroup: FormGroup;
  countries: Country[];
  states: AustralianState[];
  applicationId: number;
  isFormSubmitted = false;
  isStudentAlreadyEnrolledInAustralia: boolean;
  studentLivingWithApiError = '';
  isStudentAlreadyEnrolledInAustraliaApiError = '';
  genericErrorMsg = '';
  isSaveSuccessful = false;
  selectedStudentLivingWith = '';
  isScrollDone = false;
  isSubmittedApplication = false;
  showModal = false;

  initialStudentDetails: PersonDetails = {
    relationship: undefined,    
    otherRelationship: undefined,
    givenName: undefined,
    familyName: undefined,
    preferredName: undefined,
    isRefusedAusVisaBefore: undefined,
    visaRefusalDetails: undefined,
    dateOfBirth: undefined,
    gender: undefined,
    countryOfBirth: undefined,
    nationality: undefined,
    hasPassport: undefined,
    passportNumber: undefined,
    passportExpiryDate: undefined,
    emailAddress: undefined,
    onlyHasOneName: undefined,
  };

  initialPhoneNumber: PhoneNumberDetails = {
    mobileCountryCode: undefined,
    mobilePhoneNumber: undefined,
    homeCountryCode: undefined,
    homePhoneNumber: undefined
  };

  studentDetailsConfig: PersonDetailsConfig = {
    isRelationshipRequired: false,
    isRefusedAusVisaBefore: false,
    title: 'Student Details',
    titleAdditionalInfo: '1 of 5',
    countries: [],
    relationships: []
  };

  studentDetailsErrors: PersonDetailsErrors = {};

  studentDetailsData: PersonDetails;
  initialStudentDetailsData: PersonDetails;

  phoneNumberConfig: PhoneNumberConfig = {
    title: 'Phone Number',
    titleAdditionalInfo: '2 of 5',
    countries: [],
  };

  phoneNumberErrors: PhoneNumberErrors = {};

  phoneNumberData: PhoneNumberDetails;
  initialPhoneNumberData: PhoneNumberDetails;

  overseasAddressErrors: AddressErrors = {};
  australianAddressErrors: AddressErrors = {};

  overseasAddressConfig: AddressConfig = {
    isAustralianAddress: false,
    title: 'Overseas Address',
    titleAdditionalInfo: '4 of 5',
    idPrefix: 'overseas-address-'
  };

  overseasAddressData: Address;
  initialOverseasAddressData: Address;

  australianAddressConfig: AddressConfig = {
    isAustralianAddress: true,
    title: 'Australian Address',
    titleAdditionalInfo: '3 of 5',
    idPrefix: 'aus-address-'
  };

  australianAddressData: Address;
  initialAustralianAddressData: Address;

  initialOverseasAddressValue: Address = {
    line1: undefined,
    line2: undefined,
    suburb: undefined,
    state: undefined,
    postcode: undefined,
    country: undefined,
    isCurrentAddress: undefined
  };

  initialAustralianAddressValue: Address = {
    line1: undefined,
    line2: undefined,
    suburb: undefined,
    state: undefined,
    postcode: undefined,
    country: undefined,
    isCurrentAddress: undefined
  };

  private subscriptionsToDispose: Subscription[] = [];

  @ViewChild('resolve') el: ElementRef;

  private constrTime = Date.now();

  constructor(private formBuilder: FormBuilder,
    private cd: ChangeDetectorRef,
    private store: Store<any>,
    private router: Router,
    private modalService: ModalService,
    private formService: FormService,
    private applicationService: ApplicationService,
    private activatedRoute: ActivatedRoute,
    private utilityService: UtilityService,
    private titleService: Title) {


    this.activatedRoute.parent.params.subscribe(params => {
      this.applicationId = +params['id'];
    });

    this.studentDetailsFormGroup = this.formBuilder.group({
      studentDetails: this.initialStudentDetails,
      phoneNumber: this.initialPhoneNumber,
      australianAddress: this.initialAustralianAddressValue,
      overseasAddress: this.initialOverseasAddressValue,
      isStudentAlreadyEnrolledInAustralia: false,
      studentLivingWith: ''
    });

    console.log(`student details constructor: ${this.constrTime}`);
    console.log(`student details constructor ended: ${Date.now()}: Time elapsed: ${(Date.now() - this.constrTime).toString()}ms`);
  }

  ngOnInit() {

    setTimeout(() => {
      const sT = Date.now();
      console.log(`student-details: init start - ${sT}`);

      if (localStorage.getItem("isAgent") == "true") {
        this.titleService.setTitle(Tab_Title.Agent_StudentDetails_Title);
      } else {
        this.titleService.setTitle(Tab_Title.StudentDetails_Title);
      }

      const countries = [this.getDefaultCountry(), ...this.activatedRoute.snapshot.data['countries']];
      const nationalityCountries = countries.filter(c => c.Code !== 'AU' && c.Code !== 'NZ');

      this.studentDetailsConfig = {...this.studentDetailsConfig, countries, nationalityCountries};
      this.phoneNumberConfig = {...this.phoneNumberConfig, countries};
      this.overseasAddressConfig = {...this.overseasAddressConfig, countries};

      this.subscriptionsToDispose.push(
        this.studentDetailsFormGroup.get('australianAddress').valueChanges
        .pipe(debounceTime(250))
        .subscribe(data => {
          if (data.isCurrentAddress) {
            const overseasAddressData = { ...this.studentDetailsFormGroup.get('overseasAddress').value };
            const australianAddressData = { ...this.studentDetailsFormGroup.get('australianAddress').value };
            if (overseasAddressData.isCurrentAddress) {
              overseasAddressData.isCurrentAddress = false;
              this.overseasAddressData = overseasAddressData;
            }

            this.overseasAddressErrors.isCurrentAddress = null;

            if (australianAddressData.isCurrentAddress) {
              this.studentDetailsConfig.titleAdditionalInfo = '1 of 5';
              this.phoneNumberConfig.titleAdditionalInfo = '2 of 5';
              this.australianAddressConfig.titleAdditionalInfo = '3 of 5';
              this.overseasAddressConfig.titleAdditionalInfo = '4 of 5';
            } else {
              this.studentDetailsConfig.titleAdditionalInfo = '1 of 4';
              this.phoneNumberConfig.titleAdditionalInfo = '2 of 4';
              this.australianAddressConfig.titleAdditionalInfo = '3 of 4';
              this.overseasAddressConfig.titleAdditionalInfo = '4 of 4';
            }
          }

        })
      );

      this.subscriptionsToDispose.push(
        this.studentDetailsFormGroup.get('overseasAddress').valueChanges
        .pipe(debounceTime(250))
        .subscribe(data => {
          if (data.isCurrentAddress) {
            const australianAddressData = { ...this.studentDetailsFormGroup.get('australianAddress').value };
            if (australianAddressData.isCurrentAddress) {
              australianAddressData.isCurrentAddress = false;
              this.australianAddressData = australianAddressData;
            }

            this.australianAddressErrors.isCurrentAddress = null;
          }
        })
      );
      
      console.log(`student-details: init until the ajax calls. Time elapsed: ${(Date.now() - sT).toString()}ms`);

      this.applicationService.getStudentDetails(this.applicationId)
        .subscribe((response: SingleResultApiResponse) => {
          console.log(`student-details: getStudentDetails. Time elapsed: ${(Date.now() - sT).toString()}ms`);
          this.updateFormValuesFromStudentDetails(response.Result);
        }, err => {
      });

      this.applicationService.getApplicationDetails(this.applicationId)
        .subscribe((response: SingleResultApiResponse) => {
          if (response.Result) {

            this.isSubmittedApplication = response.Result.ApplicationStatus === 'Submitted';

            if (this.isSubmittedApplication) {
              this.studentDetailsFormGroup.disable();
              const isSubmittedApplication = true;

              this.studentDetailsConfig = { ...this.studentDetailsConfig, isSubmittedApplication };
              this.phoneNumberConfig = { ...this.phoneNumberConfig, isSubmittedApplication };
              this.australianAddressConfig = { ...this.australianAddressConfig, isSubmittedApplication };
              this.overseasAddressConfig = { ...this.overseasAddressConfig, isSubmittedApplication };
            }
          }
        }, err => {
          console.error(err);
        });
    }, 4);    
  }

  ngAfterViewInit() {
    console.log(`student details ngAfterViewInit finished: ${Date.now()}: Time elapsed since instantiation: ${(Date.now() - this.constrTime).toString()}ms`);
  }

  ngOnDestroy() {
    this.subscriptionsToDispose.forEach(s => s.unsubscribe());
  }

  getDefaultCountry(): Country {
    return {
      Id: '',
      Code: '',
      Description: 'Please select',
      Status: '',
      IsAgentMandatory: false,
      CanStudyAbroad: false,
      PhoneCode: 0,
      IsDeleted: false
    };
  }

  reloadInitialData() {

    this.applicationService.getStudentDetails(this.applicationId)
      .subscribe((response: SingleResultApiResponse) => {
        this.updateInitialData(response.Result);
      }, err => {
        console.error(err);
      });

  }


  getAustralianAddressFromStudentDetails(studentDetails: StudentDetails): Address {
    return {
      line1: studentDetails.AustralianAddressLine1,
      line2: studentDetails.AustralianAddressLine2,
      suburb: studentDetails.AustralianSuburb,
      state: studentDetails.AustralianState,
      postcode: studentDetails.AustralianPostcode,
      country: undefined,
      isCurrentAddress: studentDetails.IsAustralianAddressCurrentAddress
    };
  }

  getOverseasAddressFromStudentDetails(studentDetails: StudentDetails): Address {
    return {
      line1: studentDetails.OverseasAddressLine1,
      line2: studentDetails.OverseasAddressLine2,
      suburb: studentDetails.OverseasSuburbTown,
      state: studentDetails.OverseasStateProvinceRegion,
      postcode: studentDetails.OverseasPostcode,
      country: studentDetails.OverseasCountryId,
      isCurrentAddress: studentDetails.IsOverseasAddressCurrentAddress
    };
  }

  getPersonDetailsFromStudentDetails(studentDetails: StudentDetails): PersonDetails {
    return {
      relationship: undefined,
      otherRelationship: undefined,
      givenName: studentDetails.GivenName,
      familyName: studentDetails.FamilyName,
      preferredName: studentDetails.PreferredName,
      isRefusedAusVisaBefore: undefined,
      visaRefusalDetails: undefined,
      dateOfBirth: studentDetails.DateOfBirth,
      gender: studentDetails.Gender,
      countryOfBirth: studentDetails.CountryOfBirthId,
      nationality: studentDetails.CountryOfNationalityId,
      hasPassport: studentDetails.HasPassport,
      passportNumber: studentDetails.PassportNumber,
      passportExpiryDate: studentDetails.PassportExpiryDate,
      emailAddress: studentDetails.EmailAddress,
      onlyHasOneName: studentDetails.OnlyHasOneName,
    };
  }

  getPhoneNumberDetailsFromStudentDetails(studentDetails: StudentDetails): PhoneNumberDetails {
    return {
      mobileCountryCode: studentDetails.MobileCountryCode,
      mobilePhoneNumber: studentDetails.MobilePhoneNumber,
      homeCountryCode: studentDetails.HomeCountryCode,
      homePhoneNumber: studentDetails.HomePhoneNumber
    };
  }

  updateInitialData(studentDetails: StudentDetails): void {

    const studentDetailsData = this.getPersonDetailsFromStudentDetails(studentDetails);
    const phoneNumberData = this.getPhoneNumberDetailsFromStudentDetails(studentDetails);
    const australianAddressData = this.getAustralianAddressFromStudentDetails(studentDetails);
    const overseasAddressData = this.getOverseasAddressFromStudentDetails(studentDetails);

    this.initialStudentDetailsData = { ...studentDetailsData };
    this.initialPhoneNumberData = { ...phoneNumberData };
    this.initialAustralianAddressData = { ...australianAddressData };
    this.initialOverseasAddressData = { ...overseasAddressData };

  }

  updateFormValuesFromStudentDetails(studentDetails: StudentDetails): void {

    this.studentDetailsData = this.getPersonDetailsFromStudentDetails(studentDetails);
    this.phoneNumberData = this.getPhoneNumberDetailsFromStudentDetails(studentDetails);
    this.australianAddressData = this.getAustralianAddressFromStudentDetails(studentDetails);
    this.overseasAddressData = this.getOverseasAddressFromStudentDetails(studentDetails);

    this.initialStudentDetailsData = { ... this.studentDetailsData };
    this.initialPhoneNumberData = { ...this.phoneNumberData };
    this.initialAustralianAddressData = { ...this.australianAddressData };
    this.initialOverseasAddressData = { ...this.overseasAddressData };

    this.selectedStudentLivingWith = studentDetails.StudentLivingWith;

    this.studentDetailsFormGroup.patchValue({
      studentDetails: this.studentDetailsData,
      phoneNumber: this.phoneNumberData,
      australianAddress: this.australianAddressData,
      overseasAddress: this.overseasAddressData,
      isStudentAlreadyEnrolledInAustralia: studentDetails.IsStudentAlreadyEnrolledInAustralia,
      studentLivingWith: studentDetails.StudentLivingWith
    });

    this.isStudentAlreadyEnrolledInAustralia = studentDetails.IsStudentAlreadyEnrolledInAustralia;
    this.studentDetailsFormGroup.updateValueAndValidity();
  }

  setIsStudentAlreadyEnrolledInAustralia(isStudentAlreadyEnrolledInAustralia: boolean) {

    this.isStudentAlreadyEnrolledInAustralia = isStudentAlreadyEnrolledInAustralia;
    this.studentDetailsFormGroup.patchValue({
      isStudentAlreadyEnrolledInAustralia
    });

    this.studentDetailsFormGroup.updateValueAndValidity();
  }

  previous() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'important-information']);
  }
  next() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'english-proficiency']);
  }

  isThisFieldHasErrors(fieldName: string): boolean {
    return this.isFormSubmitted && this.studentDetailsFormGroup.get(fieldName).errors != null;
  }

  isThisFieldHasApiError(fieldName: string): boolean {
    return this.isFormSubmitted
      && this.studentDetailsFormGroup.get(fieldName).errors != null
      && this.studentDetailsFormGroup.get(fieldName).errors.apiValidationError != null;
  }



  updateStudentLivingWith(studentLivingWith: any) {
    this.selectedStudentLivingWith = studentLivingWith;
    this.studentDetailsFormGroup.patchValue({
      studentLivingWith: studentLivingWith
    });
    this.studentDetailsFormGroup.updateValueAndValidity();
  }

  convertDateToString(dateInString: string): string {
    if (!dateInString) {
      return null;
    }
    const date = new Date(dateInString);
    const dateStr = date ? `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}` : '';
    return dateStr;
  }


  saveAndContinue() {
    const studentDetails = this.getStudentDetails();
    this.isFormSubmitted = true;
    this.applicationService.updateStudentDetails(this.applicationId, studentDetails)
      .subscribe((response: SingleResultApiResponse) => {
        this.dispatchSectionSaveAction();
        this.isSaveSuccessful = true;
        if (response.Result) {
          this.showModal = response.Result.IsPassportStatusChanged || response.Result.IsAlreadyEnrolledStatusChanged ||
            response.Result.IsDateOfBirthStatusChanged || response.Result.IsNationalityStatusChanged || response.Result.IsGenderStatusChanged;
        }
        if (this.showModal === false) {
          this.router.navigate(['/applications', this.applicationId, 'edit', 'english-proficiency']);
        } else {
          setTimeout(() => {
            this.el.nativeElement.focus();
          }, 100);
        }
      }, (errorResponse: ErrorResponse) => {
        this.genericErrorMsg = handleGenericHttpError(errorResponse);
        const studentDetailsErrors: PersonDetailsErrors = {};
        const phoneNumberErrors: PhoneNumberErrors = {};
        const australianAddressErrors: AddressErrors = {};
        const overseasAddressErrors: AddressErrors = {};

        const { errors } = errorResponse;
        for (const error of errors) {
          this.setApiValidationError(error.Key,
            error.Value,
            studentDetailsErrors,
            phoneNumberErrors,
            australianAddressErrors,
            overseasAddressErrors);
        }

        this.studentDetailsErrors = studentDetailsErrors;
        this.phoneNumberErrors = phoneNumberErrors;
        this.australianAddressErrors = australianAddressErrors;
        this.overseasAddressErrors = overseasAddressErrors;
        this.studentDetailsFormGroup.updateValueAndValidity();
        this.dispatchSectionSaveAction();
        this.reloadInitialData();
        this.isScrollDone = false;
        this.formService.scrollToFirstErrorField();
      });



  }

  displayModal(): boolean {
    return this.showModal;
  }

  clickOK() {
    this.showModal = false;
    this.router.navigate(['/applications', this.applicationId, 'edit', 'english-proficiency']);
  }

  ngAfterContentChecked(): void {

    // if (!this.isScrollDone) {
    //   this.isScrollDone = this.formService.scrollToFirstErrorField();
    // }

  }


  dispatchSectionSaveAction() {
    this.store.dispatch({
      type: Action_ApplicationSectionSaved
    });
  }

  setApiValidationError(apiErrorKeyName: string,
    apiErrorMsg: string,
    studentDetailsErrors: PersonDetailsErrors,
    phoneNumberErrors: PhoneNumberErrors,
    australianAddressErrors: AddressErrors,
    overseasAddressErrors: AddressErrors): void {
    //console.log(`In setApiValidationError - apiErrorKeyName:${apiErrorKeyName}, apiErrorMsg:${apiErrorMsg}`);


    switch (apiErrorKeyName) {
      case 'GivenName':
        studentDetailsErrors.givenName = apiErrorMsg;
        break;
      case 'FamilyName':
        studentDetailsErrors.familyName = apiErrorMsg;
        break;
      case 'PreferredName':
        studentDetailsErrors.preferredName = apiErrorMsg;
        break;
      case 'Gender':
        studentDetailsErrors.gender = apiErrorMsg;
        break;
      case 'DateOfBirth':
        studentDetailsErrors.dateOfBirth = apiErrorMsg;
        break;
      case 'CountryOfBirthId':
        studentDetailsErrors.countryOfBirth = apiErrorMsg;
        break;
      case 'CountryOfNationalityId':
        studentDetailsErrors.nationality = apiErrorMsg;
        break;
      case 'HasPassport':
        studentDetailsErrors.hasPassport = apiErrorMsg;
        break;
      case 'PassportNumber':
        studentDetailsErrors.passportNumber = apiErrorMsg;
        break;
      case 'PassportExpiryDate':
        studentDetailsErrors.passportExpiryDate = apiErrorMsg;
        break;
      case 'EmailAddress':
        studentDetailsErrors.emailAddress = apiErrorMsg;
        break;
      case 'MobileCountryCode':
        phoneNumberErrors.mobileCountryCode = apiErrorMsg;
        break;
      case 'MobilePhoneNumber':
        phoneNumberErrors.mobilePhoneNumber = apiErrorMsg;
        break;
      case 'HomeCountryCode':
        phoneNumberErrors.homeCountryCode = apiErrorMsg;
        break;
      case 'HomePhoneNumber':
        phoneNumberErrors.homePhoneNumber = apiErrorMsg;
        break;
      case 'AustralianAddressLine1':
        australianAddressErrors.line1 = apiErrorMsg;
        break;
      case 'AustralianAddressLine2':
        australianAddressErrors.line2 = apiErrorMsg;
        break;
      case 'AustralianSuburb':
        australianAddressErrors.suburb = apiErrorMsg;
        break;
      case 'AustralianState':
        australianAddressErrors.state = apiErrorMsg;
        break;
      case 'AustralianPostcode':
        australianAddressErrors.postcode = apiErrorMsg;
        break;
      case 'IsAustralianAddressCurrentAddress':
        australianAddressErrors.isCurrentAddress = apiErrorMsg;
        break;
      case 'OverseasAddressLine1':
        overseasAddressErrors.line1 = apiErrorMsg;
        break;
      case 'OverseasAddressLine2':
        overseasAddressErrors.line2 = apiErrorMsg;
        break;
      case 'OverseasSuburbTown':
        overseasAddressErrors.suburb = apiErrorMsg;
        break;
      case 'OverseasStateProvinceRegion':
        overseasAddressErrors.state = apiErrorMsg;
        break;
      case 'OverseasPostcode':
        overseasAddressErrors.postcode = apiErrorMsg;
        break;
      case 'OverseasCountryId':
        overseasAddressErrors.country = apiErrorMsg;
        break;
      case 'IsOverseasAddressCurrentAddress':
        overseasAddressErrors.isCurrentAddress = apiErrorMsg;
        break;
      case 'IsStudentAlreadyEnrolledInAustralia':
        this.studentDetailsFormGroup.get('isStudentAlreadyEnrolledInAustralia').setErrors({ apiValidationError: true });
        this.isStudentAlreadyEnrolledInAustraliaApiError = apiErrorMsg;
        break;
      case 'StudentLivingWith':
        this.studentDetailsFormGroup.get('studentLivingWith').setErrors({ apiValidationError: true });
        this.studentLivingWithApiError = apiErrorMsg;
        break;

    }


  }

  updateStudentPersonDetails(studentDetails: StudentDetails, personDetails: PersonDetails) {
    studentDetails.GivenName = personDetails.givenName;
    studentDetails.FamilyName = personDetails.familyName;
    studentDetails.OnlyHasOneName = personDetails.onlyHasOneName;
    studentDetails.CountryOfBirthId = personDetails.countryOfBirth;
    studentDetails.CountryOfNationalityId = personDetails.nationality;
    studentDetails.PreferredName = personDetails.preferredName;
    studentDetails.HasPassport = personDetails.hasPassport;
    studentDetails.PassportNumber = personDetails.passportNumber;
    studentDetails.PassportExpiryDate = this.utilityService.getDateStrWithoutTimeZone(personDetails.passportExpiryDate);
    studentDetails.Gender = personDetails.gender;
    studentDetails.EmailAddress = personDetails.emailAddress;
    studentDetails.DateOfBirth = this.utilityService.getDateStrWithoutTimeZone(personDetails.dateOfBirth);
  }

  updateStudentPhoneNumberDetails(studentDetails: StudentDetails, phoneNumberDetails: PhoneNumberDetails) {

    studentDetails.MobileCountryCode = phoneNumberDetails.mobileCountryCode === '0' ? null : phoneNumberDetails.mobileCountryCode;
    studentDetails.MobilePhoneNumber = phoneNumberDetails.mobilePhoneNumber;
    studentDetails.HomeCountryCode = phoneNumberDetails.homeCountryCode === '0' ? null : phoneNumberDetails.homeCountryCode;
    studentDetails.HomePhoneNumber = phoneNumberDetails.homePhoneNumber;
  }

  updateStudentAddress(studentDetails: StudentDetails, address: Address, isAustralianAddress: boolean) {

    const { line1, line2, suburb, state, country, postcode, isCurrentAddress } = address;

    if (isAustralianAddress) {
      studentDetails.AustralianAddressLine1 = line1;
      studentDetails.AustralianAddressLine2 = line2;
      studentDetails.AustralianSuburb = suburb;
      studentDetails.AustralianState = state;
      studentDetails.AustralianPostcode = postcode;
      studentDetails.IsAustralianAddressCurrentAddress = isCurrentAddress;
    } else {
      studentDetails.OverseasAddressLine1 = line1;
      studentDetails.OverseasAddressLine2 = line2;
      studentDetails.OverseasSuburbTown = suburb;
      studentDetails.OverseasStateProvinceRegion = state;
      studentDetails.OverseasCountryId = country;
      studentDetails.OverseasPostcode = postcode;
      studentDetails.IsOverseasAddressCurrentAddress = isCurrentAddress;
    }

  }

  getStudentDetails(): StudentDetails {
    const studentDetails = new StudentDetails();

    const studentPersonDetails = this.studentDetailsFormGroup.get('studentDetails').value;
    this.updateStudentPersonDetails(studentDetails, studentPersonDetails);
    const phoneNumberDetails = this.studentDetailsFormGroup.get('phoneNumber').value;
    this.updateStudentPhoneNumberDetails(studentDetails, phoneNumberDetails);
    const australianAddress = this.studentDetailsFormGroup.get('australianAddress').value;
    this.updateStudentAddress(studentDetails, australianAddress, true);
    const overseasAddress = this.studentDetailsFormGroup.get('overseasAddress').value;
    this.updateStudentAddress(studentDetails, overseasAddress, false);

    studentDetails.IsStudentAlreadyEnrolledInAustralia = this.studentDetailsFormGroup.get('isStudentAlreadyEnrolledInAustralia').value;
    if (!studentDetails.IsStudentAlreadyEnrolledInAustralia) {
      studentDetails.StudentLivingWith = '';
    }
    else {
      studentDetails.StudentLivingWith = this.studentDetailsFormGroup.get('studentLivingWith').value;
    }

    return studentDetails;
  }

  canDeactivate(): Observable<boolean> | boolean {

    if (this.isSaveSuccessful) {
      return true;
    }

    if (!this.isStudentDetailsSame()) {
      this.modalService.showModal();
      return this.modalService.stayOnThePage;
    } else {
      this.modalService.hideModal();
      return true;
    }
  }

  isStudentDetailsSame(): boolean {
    const currentPersonDetails = this.studentDetailsFormGroup.get('studentDetails').value;
    const currentPhoneNumberDetails = this.studentDetailsFormGroup.get('phoneNumber').value;
    const currentAustralianAddressDetails = this.studentDetailsFormGroup.get('australianAddress').value;
    const currentOverseasAddressDetails = this.studentDetailsFormGroup.get('overseasAddress').value;

    const isStudentDetailsSame = compareStudentDetails(this.initialStudentDetailsData, currentPersonDetails,
      this.initialPhoneNumberData, currentPhoneNumberDetails,
      this.initialAustralianAddressData, currentAustralianAddressDetails,
      this.initialOverseasAddressData, currentOverseasAddressDetails);

    return isStudentDetailsSame;
  }
}

